import { ApiResponse } from './api-response.models';

/**
 * Two-step verification primitives shared across features that gate a
 * mutation behind a verification-management OTP / TOTP check.
 *
 * Consumers today:
 *   - Transactions  — `POST /transaction/:id/broadcast`
 *   - Approvals     — `POST /approval-requests/:id/approve | /reject`
 *
 * Each feature owns its own initial call (`/broadcast`, `/approve`, …) and
 * its own confirmation call. Only the verification step in the middle —
 * `POST /api/v1/verification/verify` on verification-management — is
 * shared, so all of the wire shapes around that step live here.
 */

/**
 * Channel selected by the backend to deliver the verification code.
 *
 * Returned inside the 202 `verification_required` response from any
 * mutation that demands a second factor.
 */
export enum VerificationMethod {
  SMS = 'SMS',
  EMAIL = 'EMAIL',
  GAUTH = 'GAUTH',
}

/**
 * 202 response returned by feature mutations (broadcast / approve /
 * reject) when verification is required.
 *
 * `expiresAt` is normalized to a Unix-millisecond `number` by
 * {@link VerificationService.normalizeVerificationRequired} — some
 * backends serialize it as a Long-shaped object (`{ low, high, unsigned }`)
 * and we coerce it once at the service layer so consumers always see a
 * plain number.
 */
export interface VerificationRequiredResponse {
  statusCode: 202;
  message: 'verification_required' | string;
  actionId: string;
  verificationMethod: VerificationMethod;
  /** Unix milliseconds when the verification window expires. */
  expiresAt: number;
}

/** Body for `POST /api/v1/verification/verify` on verification-management. */
export interface VerifyCodeRequest {
  actionId: string;
  code: string;
}

/** Inner data for `POST /api/v1/verification/verify`. */
export interface VerifyCodeData {
  verified: boolean;
  actionId: string;
  attemptsRemaining: number;
}

/**
 * What `POST /api/v1/verification/verify` answers with. verification-management
 * registers no success envelope, so the body is `VerifyCodeData` itself; the
 * enveloped form is accepted too in case it adopts the shared one. Read it
 * through `readVerifyCodeAnswer`, never as `response.data`.
 */
export type VerifyCodeResponse = VerifyCodeData | ApiResponse<VerifyCodeData>;

/**
 * The verdict inside a verify answer, bare or enveloped, or `null` when there
 * is none. Reading `response.data` alone found nothing in the bare body, so
 * every correct code was reported as wrong once F-054 required an explicit
 * `verified: true`.
 */
export function readVerifyCodeAnswer(
  response: VerifyCodeResponse | null | undefined
): Partial<VerifyCodeData> | null {
  if (!response || typeof response !== 'object') {
    return null;
  }
  if ('data' in response) {
    const data = (response as ApiResponse<VerifyCodeData>).data;
    return data && typeof data === 'object' ? data : null;
  }
  return response as Partial<VerifyCodeData>;
}

/**
 * Identifies which feature flow asked for the verification, so the shared
 * NgRx feature can surface a meaningful `source` to consumers without
 * coupling the verification slice to feature-specific knowledge.
 *
 * Kept as a string union (rather than a strict enum) so new feature
 * sources can plug in without touching the shared module.
 */
export type VerificationSource = 'broadcast' | 'approve' | 'reject' | string;
